// ── Welle B4: Für Gespräch und Konferenz ─────────────────────────────────────────
// Drei Fenster im K4-Rahmen, je eins für die ganze App (KonferenzHosts am App-Root):
//  · „Was soll aufs Blatt?“ — Abschnitte wählen (je Gerät gemerkt), dann das Blatt als PDF
//  · „Konferenzliste“ — Halbjahr und Spalten wählen, dann PDF (A4 quer)
//  · „AV/SV-Vorschläge“ — je Kind zwei Werte 1–6 und ein kurzer Satz, ein Speichern für alle
// Die PDFs baut der Server bei Bedarf (nichts wird gespeichert); alle Werte kommen von dort.
import React, { useCallback, useEffect, useState } from 'react';
import { schuljahrKurz } from '../../lib/schuljahr';
import { MIcon } from '../../lib/micon';
import { API_BASE, apiHeaders, apiGet, apiPut, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { AVSV_EVENT, BLATT_EVENT, BlattZiel, KONFERENZ_EVENT, KonferenzZiel, oeffneAvSv } from '../../lib/nav';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFensterFuss, LdSchalterZeile, LdUmschalter } from '../../components/ld/LdFenster';
import './Konferenz.css';

export const STUFEN = ['sehr gut', 'gut', 'befriedigend', 'ausreichend', 'mangelhaft', 'ungenügend'];
const BEMERKUNG_MAX = 140;

/** Halbjahr heute: Aug–Jan = 1, Feb–Jul = 2 (wie der Server) */
export const aktuellesHalbjahr = (d = new Date()): 1 | 2 => (d.getMonth() >= 7 || d.getMonth() === 0 ? 1 : 2);

/** Lokaler Speicher kann fehlen (privates Fenster) — dann gilt die Vorgabe */
function lies<T>(schluessel: string, vorgabe: T): T {
  try { const v = localStorage.getItem(schluessel); return v ? { ...vorgabe, ...JSON.parse(v) } : vorgabe; } catch { return vorgabe; }
}
function merke(schluessel: string, wert: unknown) { try { localStorage.setItem(schluessel, JSON.stringify(wert)); } catch { /* egal */ } }

/** PDF vom Server holen und speichern; Dateiname vom Server (ohne vollen Namen) */
async function pdfHolen(pfad: string, ersatzName: string) {
  const r = await fetch(`${API_BASE}${pfad}`, { headers: apiHeaders() });
  if (!r.ok) {
    const j = await r.json().catch(() => null);
    throw new Error(j?.fehler ?? 'Das PDF konnte nicht erstellt werden.');
  }
  const name = decodeURIComponent(r.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? ersatzName);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a'); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function useZiel<T>(ereignis: string) {
  const [ziel, setZiel] = useState<T | null>(null);
  useEffect(() => {
    const h = (e: Event) => setZiel((e as CustomEvent<T>).detail ?? null);
    window.addEventListener(ereignis, h);
    return () => window.removeEventListener(ereignis, h);
  }, [ereignis]);
  return [ziel, setZiel] as const;
}

export function KonferenzHosts() {
  const [blatt, setBlatt] = useZiel<BlattZiel>(BLATT_EVENT);
  const [liste, setListe] = useZiel<KonferenzZiel>(KONFERENZ_EVENT);
  const [avsv, setAvsv] = useZiel<KonferenzZiel & { halbjahr?: 1 | 2 }>(AVSV_EVENT);
  return (
    <>
      <BlattFenster ziel={blatt} onClose={() => setBlatt(null)} />
      <KonferenzFenster ziel={liste} onClose={() => setListe(null)} />
      <AvSvListe ziel={avsv} onClose={() => setAvsv(null)} />
    </>
  );
}

// ── Blatt fürs Gespräch ─────────────────────────────────────────────────────────
const ABSCHNITTE = [
  { key: 'noten', icon: 'bar_chart', titel: 'Noten', text: 'Schriftlich, mündlich, Halbjahresnote je Fach' },
  { key: 'fehlzeiten', icon: 'event_busy', titel: 'Fehlzeiten', text: 'Stunden, Tage, offene Entschuldigungen, Attest' },
  { key: 'mitarbeit', icon: 'back_hand', titel: 'Mitarbeit', text: 'Durchschnitt und beobachtete Wochen' },
  { key: 'gespraeche', icon: 'forum', titel: 'Letzte Gespräche', text: 'Höchstens 3, mit Vereinbarungen' },
  { key: 'notizen', icon: 'sticky_note_2', titel: 'Letzte Notizen', text: 'Höchstens 5, ohne Archiv' },
  { key: 'zeugnis', icon: 'description', titel: 'Zeugnis-Text', text: 'Wenn für dieses Halbjahr gespeichert' },
] as const;
type Abschnitt = typeof ABSCHNITTE[number]['key'];
const BLATT_SPEICHER = 'ld-blatt-abschnitte';
const alleAn = Object.fromEntries(ABSCHNITTE.map(a => [a.key, true])) as Record<Abschnitt, boolean>;

function BlattFenster({ ziel, onClose }: { ziel: BlattZiel | null; onClose: () => void }) {
  const [an, setAn] = useState<Record<Abschnitt, boolean>>(alleAn);
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { if (ziel) setAn(lies(BLATT_SPEICHER, alleAn)); }, [ziel]);
  const gewaehlt = ABSCHNITTE.filter(a => an[a.key]).map(a => a.key);

  async function erstellen() {
    if (!ziel) return;
    merke(BLATT_SPEICHER, an);
    setLaeuft(true);
    try {
      await pdfHolen(`/api/export/schueler/${ziel.schuelerId}?abschnitte=${gewaehlt.join(',')}`, 'Blatt.pdf');
      onClose();
    } catch (e) { zeigeApiFehler(e, 'Das Blatt konnte nicht erstellt werden'); }
    finally { setLaeuft(false); }
  }

  return (
    <LdSheet open={!!ziel} onClose={onClose} maxWidth={480}
      kopf={{ titel: 'Was soll aufs Blatt?', kontext: ziel ? `${ziel.name}, eine Seite fürs Gespräch` : null }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt={<><MIcon name="picture_as_pdf" size={18} />PDF erstellen</>} onHaupt={erstellen} aus={!gewaehlt.length} laden={laeuft} />}>
      <div className="ld-kf-schalter">
        {ABSCHNITTE.map(a => (
          <LdSchalterZeile key={a.key} icon={a.icon} titel={a.titel} text={a.text} an={an[a.key]} onChange={v => setAn(x => ({ ...x, [a.key]: v }))} />
        ))}
      </div>
      <p className="ld-kf-hinweis">Das Blatt kann Eltern gezeigt werden. Notizen nur mitnehmen, wenn sie dafür gedacht sind.</p>
    </LdSheet>
  );
}

// ── Konferenzliste ──────────────────────────────────────────────────────────────
const SPALTEN = [
  { key: 'noten', titel: 'Halbjahresnoten', text: 'Je eigenes Fach; Fächer der Kolleginnen bleiben leer' },
  { key: 'fehlzeiten', titel: 'Fehlstunden', text: 'Davon unentschuldigt, laufendes Schuljahr' },
  { key: 'paragraph68', titel: '§68', text: 'Wenn deine Schwelle erreicht ist' },
  { key: 'noten56', titel: 'Anzahl Noten 5 und 6', text: 'Nur feststehende Noten' },
  { key: 'av', titel: 'AV', text: 'Arbeitsverhalten, dein Vorschlag' },
  { key: 'sv', titel: 'SV', text: 'Sozialverhalten, dein Vorschlag' },
  { key: 'bemerkung', titel: 'Bemerkung', text: 'Dein kurzer Satz je Kind' },
] as const;
type Spalte = typeof SPALTEN[number]['key'];
const LISTE_SPEICHER = 'ld-konferenz-spalten';
const alleSpalten = Object.fromEntries(SPALTEN.map(s => [s.key, true])) as Record<Spalte, boolean>;

function KonferenzFenster({ ziel, onClose }: { ziel: KonferenzZiel | null; onClose: () => void }) {
  const [an, setAn] = useState<Record<Spalte, boolean>>(alleSpalten);
  const [halbjahr, setHalbjahr] = useState<'1' | '2'>(String(aktuellesHalbjahr()) as '1' | '2');
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { if (ziel) { setAn(lies(LISTE_SPEICHER, alleSpalten)); setHalbjahr(String(aktuellesHalbjahr()) as '1' | '2'); } }, [ziel]);
  const gewaehlt = SPALTEN.filter(s => an[s.key]).map(s => s.key);

  async function erstellen() {
    if (!ziel) return;
    merke(LISTE_SPEICHER, an);
    setLaeuft(true);
    try {
      await pdfHolen(`/api/export/konferenz/${ziel.klasseId}?halbjahr=${halbjahr}&spalten=${gewaehlt.join(',')}`, `Konferenzliste-HJ${halbjahr}.pdf`);
      onClose();
    } catch (e) { zeigeApiFehler(e, 'Die Konferenzliste konnte nicht erstellt werden'); }
    finally { setLaeuft(false); }
  }

  return (
    <LdSheet open={!!ziel} onClose={onClose} maxWidth={500}
      kopf={{ titel: 'Konferenzliste', kontext: ziel ? `Klasse ${ziel.klasseName}, eine Zeile je Kind, A4 quer` : null }}
      fuss={<>
        {/* am Handy steht der Link in einer eigenen Zeile über den Knöpfen */}
        {ziel && <span className="ld-fe-fuss__links ld-fe-fuss__links--zeile">
          <button type="button" className="ld-kl-link" onClick={() => { onClose(); oeffneAvSv({ ...ziel, halbjahr: Number(halbjahr) as 1 | 2 }); }}>AV/SV-Vorschläge eintragen</button>
        </span>}
        <LdFensterFuss onAbbrechen={onClose} haupt={<><MIcon name="picture_as_pdf" size={18} />PDF erstellen</>} onHaupt={erstellen} laden={laeuft} />
      </>}>
      <div className="ld-kf-hj">
        <LdUmschalter aria-label="Halbjahr" wert={halbjahr} onChange={setHalbjahr}
          optionen={[{ wert: '1', label: '1. Halbjahr' }, { wert: '2', label: '2. Halbjahr' }]} />
      </div>
      <div className="ld-kf-schalter">
        {SPALTEN.map(s => (
          <LdSchalterZeile key={s.key} titel={s.titel} text={s.text} an={an[s.key]} onChange={v => setAn(x => ({ ...x, [s.key]: v }))} />
        ))}
      </div>
      <p className="ld-kf-hinweis">Name steht immer vorn. Die Liste ist vertraulich und nur für die Klassenkonferenz.</p>
    </LdSheet>
  );
}

// ── AV/SV-Vorschläge ────────────────────────────────────────────────────────────
type KindVorschlag = { schueler_id: number; vorname: string; nachname: string; av: number | null; sv: number | null; bemerkung: string | null };
type Liste = { klasse_id: number; klasse_name: string; halbjahr: 1 | 2; schuljahr: string; kinder: KindVorschlag[] };
type Zeile = { av: number | null; sv: number | null; bemerkung: string; offen: boolean };
const zeileAus = (k: KindVorschlag): Zeile => ({ av: k.av, sv: k.sv, bemerkung: k.bemerkung ?? '', offen: !!k.bemerkung });

function StufenWahl({ art, wert, onChange, name }: { art: 'AV' | 'SV'; wert: number | null; onChange: (n: number | null) => void; name: string }) {
  return (
    <div className="ld-avsv__wahl">
      <span className="ld-avsv__art" title={art === 'AV' ? 'Arbeitsverhalten' : 'Sozialverhalten'}>{art}</span>
      <span className="ld-avsv__stufen" role="radiogroup" aria-label={`${art === 'AV' ? 'Arbeitsverhalten' : 'Sozialverhalten'} von ${name}`}>
        {[1, 2, 3, 4, 5, 6].map(n => (
          <button key={n} type="button" role="radio" aria-checked={wert === n} aria-label={`${STUFEN[n - 1]} (${n})`}
            className={wert === n ? 'ist-an' : ''} onClick={() => onChange(wert === n ? null : n)}>{n}</button>
        ))}
      </span>
      <span className="ld-avsv__wort" aria-live="polite">{wert ? STUFEN[wert - 1] : ''}</span>
    </div>
  );
}

function AvSvListe({ ziel, onClose }: { ziel: (KonferenzZiel & { halbjahr?: 1 | 2 }) | null; onClose: () => void }) {
  const [liste, setListe] = useState<Liste | null>(null);
  const [fehler, setFehler] = useState('');
  const [zeilen, setZeilen] = useState<Record<number, Zeile>>({});
  const [speichert, setSpeichert] = useState(false);

  const laden = useCallback(async (z: KonferenzZiel & { halbjahr?: 1 | 2 }) => {
    setFehler(''); setListe(null);
    try {
      const l = await apiGet<Liste>(`/api/klassen/${z.klasseId}/av-sv?halbjahr=${z.halbjahr ?? aktuellesHalbjahr()}`);
      setListe(l); setZeilen(Object.fromEntries(l.kinder.map(k => [k.schueler_id, zeileAus(k)])));
    } catch (e) { setFehler(e instanceof Error ? e.message : 'Die Liste konnte nicht geladen werden.'); }
  }, []);
  useEffect(() => { if (ziel) laden(ziel); }, [ziel, laden]);

  const setze = (id: number, teil: Partial<Zeile>) => setZeilen(z => ({ ...z, [id]: { ...z[id], ...teil } }));
  const geaendert = liste ? liste.kinder.filter(k => {
    const z = zeilen[k.schueler_id]; return z && (z.av !== k.av || z.sv !== k.sv || z.bemerkung.trim() !== (k.bemerkung ?? ''));
  }) : [];
  const mitVorschlag = liste ? liste.kinder.filter(k => zeilen[k.schueler_id]?.av || zeilen[k.schueler_id]?.sv).length : 0;

  async function speichern() {
    if (!liste) return;
    if (!geaendert.length) { onClose(); return; }
    setSpeichert(true);
    try {
      await apiPut(`/api/klassen/${liste.klasse_id}/av-sv`, { halbjahr: liste.halbjahr,
        eintraege: geaendert.map(k => { const z = zeilen[k.schueler_id]; return { schueler_id: k.schueler_id, av: z.av, sv: z.sv, bemerkung: z.bemerkung.trim() || null }; }) });
      toast(`Vorschläge gespeichert · ${geaendert.length} ${geaendert.length === 1 ? 'Kind' : 'Kinder'}`, 'success');
      onClose();
    } catch (e) { zeigeApiFehler(e, 'Die Vorschläge konnten nicht gespeichert werden'); }
    finally { setSpeichert(false); }
  }

  return (
    <LdSheet open={!!ziel} onClose={onClose} maxWidth={620}
      kopf={{ titel: `AV/SV-Vorschläge${liste ? ` ${liste.klasse_name}` : ziel ? ` ${ziel.klasseName}` : ''}`,
        kontext: liste ? `Vorschlag für die Klassenkonferenz, ${liste.halbjahr}. Halbjahr ${schuljahrKurz(liste.schuljahr)}` : 'Vorschlag für die Klassenkonferenz' }}
      fuss={liste && <>
        <span className="ld-fe-fuss__links ld-avsv__stand">{mitVorschlag} von {liste.kinder.length} mit Vorschlag</span>
        <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={speichern} disabled={speichert}>
          <MIcon name="check" size={18} />{speichert ? 'Speichern …' : 'Alle speichern'}</button>
      </>}>
      <div className="ld-avsv">
        {fehler ? <div className="ld-pu-fehler" role="alert"><span>{fehler}</span></div>
          : !liste ? <div className="ld-pu-laedt"><span /><span /></div>
          : (<>
            <p className="ld-avsv__regel">Arbeitsverhalten (AV) und Sozialverhalten (SV): 1 sehr gut bis 6 ungenügend. Die Konferenz entscheidet.</p>
            <ol className="ld-avsv__liste">
              {liste.kinder.map(k => {
                const z = zeilen[k.schueler_id] ?? zeileAus(k);
                const name = `${k.vorname} ${k.nachname}`;
                return (
                  <li key={k.schueler_id} className="ld-avsv__kind">
                    <div className="ld-avsv__name">
                      <b>{k.nachname}, {k.vorname}</b>
                      <button type="button" className="ld-avsv__satz-knopf" aria-label={`Satz zu ${k.vorname}`} aria-expanded={z.offen}
                        onClick={() => setze(k.schueler_id, { offen: !z.offen })}><MIcon name={z.bemerkung ? 'sticky_note_2' : 'add_comment'} size={18} /></button>
                    </div>
                    <StufenWahl art="AV" wert={z.av} name={name} onChange={n => setze(k.schueler_id, { av: n })} />
                    <StufenWahl art="SV" wert={z.sv} name={name} onChange={n => setze(k.schueler_id, { sv: n })} />
                    {z.offen && (
                      <input className="ld-avsv__satz" value={z.bemerkung} maxLength={BEMERKUNG_MAX} placeholder="Kurzer Satz (optional)" aria-label={`Satz zu ${k.vorname}`}
                        onChange={e => setze(k.schueler_id, { bemerkung: e.target.value })} />
                    )}
                  </li>
                );
              })}
            </ol>
          </>)}
      </div>
    </LdSheet>
  );
}
